// ============================================================
// HandoutLightbox (v8.363.10) - one handout, large.
//
// An image gets the whole window and the ImageViewer (fit, 1:1, wheel,
// pan, fullscreen); a PDF is the browser's own viewer in an iframe; a note is its markdown; a
// link is a card that opens in a new tab. A file that was not carried in
// an import says so instead of a broken picture.
// ============================================================
import common from '../styles/common.module.css';
import styles from '../styles/HandoutsDrawer.module.css';
import clsx from '../utils/clsx';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import useFileUrl from '../hooks/useFileUrl';
import MarkdownNote from './MarkdownNote';
import ImageViewer from './ImageViewer';
import type { Handout } from '../engine/handouts';

interface Props {
  handout: Handout;
  resolve: (h: Handout, field: 'file' | 'thumb') => Promise<string>;
  /** Closes it for THIS viewer; a presentation stays up for the others. */
  onClose: () => void;
  /** The GM has put this one up on every screen in its audience (v9.8.0). */
  presented?: boolean;
  /** The GM's: take it down for everyone. */
  onEndPresent?: () => void;
}

/** Why there is no picture: the file was never carried, or it would not load (with a retry). */
function FileTrouble({ handout, error, retry }: { handout: Handout; error: string | null; retry: () => void }) {
  if (!handout.file) {
    return (
      <p className={styles.notCarried} data-testid="handout-not-carried">
        This {handout.kind === 'pdf' ? 'PDF' : 'image'} was not carried in the session file. Post it again.
      </p>
    );
  }
  return (
    error && (
      <p className={styles.notCarried}>
        Could not load the file ({error}).{' '}
        <button type="button" className={common.btnSmall} onClick={retry}>
          Try again
        </button>
      </p>
    )
  );
}

function LinkCard({ handout }: { handout: Handout }) {
  return (
    <a
      href={handout.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.linkCard}
      data-testid="handout-link"
    >
      <span className={styles.linkTitle}>{handout.title || handout.url}</span>
      <span className={styles.linkUrl}>{handout.url}</span>
    </a>
  );
}

/** The handout itself, by kind; a file-backed kind waits for its URL. */
function HandoutContent({ handout, url, retry }: { handout: Handout; url: string | null; retry: () => void }) {
  switch (handout.kind) {
    case 'image':
      return url && <ImageViewer src={url} alt={handout.title} onError={retry} />;
    case 'pdf':
      return (
        url && <iframe src={url} title={handout.title || 'PDF'} className={styles.pdf} data-testid="handout-pdf" />
      );
    case 'note':
      return <MarkdownNote source={handout.notes} />;
    case 'link':
      return <LinkCard handout={handout} />;
    default:
      return null;
  }
}

/** The title, the mark of a presentation with the GM's way to end it, and this viewer's close. */
function LightboxHead({ handout, onClose, presented, onEndPresent }: Omit<Props, 'resolve'>) {
  return (
    <div className={styles.lightboxHead}>
      <strong>{handout.title || handout.kind}</strong>
      {presented && (
        <span className={styles.presentedTag} data-testid="lightbox-presented">
          The GM is showing this
        </span>
      )}
      {presented && onEndPresent && (
        <button
          type="button"
          className={common.btnSmall}
          onClick={onEndPresent}
          title="Close it on every screen"
          data-testid="lightbox-present-end"
        >
          Stop showing
        </button>
      )}
      <button
        type="button"
        className={common.btnSmall}
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
      >
        ✕
      </button>
    </div>
  );
}

export default function HandoutLightbox({ handout, resolve, onClose, presented = false, onEndPresent }: Props) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(onClose, trapRef);
  useEscapeKey(true, onClose);
  const isImage = handout.kind === 'image';
  const needsFile = handout.kind === 'image' || handout.kind === 'pdf';
  const { url, error, retry } = useFileUrl(needsFile ? handout.file : '', (ref) =>
    resolve({ ...handout, file: ref }, 'file'),
  );
  return (
    <div
      className={common.modal}
      onClick={backdrop.onBackdropClick}
      role="presentation"
      data-testid="handout-lightbox"
      data-presented={presented || undefined}
    >
      <div
        ref={backdrop.boxRef}
        className={clsx(common.modalContent, styles.lightbox, isImage && styles.lightboxFull)}
        role="dialog"
        aria-modal="true"
        aria-label={handout.title || handout.kind}
      >
        <LightboxHead handout={handout} onClose={onClose} presented={presented} onEndPresent={onEndPresent} />
        <div className={styles.lightboxBody}>
          {needsFile && <FileTrouble handout={handout} error={error} retry={retry} />}
          <HandoutContent handout={handout} url={url} retry={retry} />
          {handout.kind !== 'note' && handout.notes && <MarkdownNote source={handout.notes} testId="handout-caption" />}
        </div>
      </div>
    </div>
  );
}
